/**
 * LeadFormSubcomponents.jsx
 * Subcomponentes internos do LeadFormModal extraídos para reduzir tamanho do arquivo principal.
 * Todos são privados — não exportar para uso externo sem necessidade.
 */
import { useState, useRef, useId } from 'react';
import { X, Plus, Building2, Search, ArrowUpRight } from 'lucide-react';
import { api } from '../../services/api';
import { formatCnpj } from '../../utils/masks';

// ─── Currency helpers ─────────────────────────────────────────────────────────

export function parseCurrency(v) {
  const clean = String(v).replace(/[^\d,]/g, '').replace(',', '.');
  return parseFloat(clean) || 0;
}

export function formatBRL(v) {
  if (v === '' || v === null || v === undefined) return '';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
}

// ─── CurrencyInput ────────────────────────────────────────────────────────────

export function CurrencyInput({ value, onChange, className, placeholder = 'R$ 0,00', id }) {
  const [display, setDisplay] = useState('');
  const [focused, setFocused] = useState(false);

  if (!focused && display !== (value ? formatBRL(value) : '')) {
    setDisplay(value ? formatBRL(value) : '');
  }

  function handleFocus() {
    setFocused(true);
    setDisplay(value ? String(Number(value).toFixed(2)).replace('.', ',') : '');
  }

  function handleChange(e) {
    setDisplay(e.target.value);
    onChange(parseCurrency(e.target.value));
  }

  function handleBlur() {
    setFocused(false);
    const num = parseCurrency(display);
    onChange(num);
    setDisplay(num ? formatBRL(num) : '');
  }

  return (
    <input
      id={id}
      type="text"
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
    />
  );
}

// ─── NovaContaModal ───────────────────────────────────────────────────────────

export function NovaContaModal({ hints = {}, onSave, onClose }) {
  const ncId = useId();
  const [form, setForm] = useState({
    nome: hints.razao_social || hints.nome_fantasia || '',
    cnpj: hints.cnpj || '',
    cidade: hints.cidade || '',
    estado: hints.estado || '',
    telefone_empresa: hints.telefone_empresa || '',
    esn_id: hints.esn_id || null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.nome.trim()) return setError('Nome é obrigatório');
    if (!form.cnpj.replace(/\D/g, '')) return setError('CNPJ é obrigatório');
    setSaving(true); setError('');
    try {
      const result = await api.createConta({
        nome: form.nome.trim(), razao_social: form.nome.trim(),
        cnpj: form.cnpj, cidade: form.cidade || null,
        estado: form.estado || null, telefone_empresa: form.telefone_empresa || null,
        esn_id: form.esn_id || null,
      });
      onSave(result);
    } catch (err) {
      setError(err.message.includes('409') || err.message.toLowerCase().includes('cnpj')
        ? 'CNPJ já cadastrado no banco de clientes'
        : err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center gap-3 p-5 border-b">
          <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
            <Building2 size={18} className="text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Novo cliente de base</h3>
            <p className="text-xs text-gray-400">Será adicionado ao Banco de Dados de Clientes</p>
          </div>
          <button type="button" onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          <div>
            <label htmlFor={`${ncId}-nome`} className="block text-xs font-medium text-gray-600 mb-1">Razão Social / Nome *</label>
            <input id={`${ncId}-nome`} value={form.nome} onChange={e => set('nome', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
          </div>
          <div>
            <label htmlFor={`${ncId}-cnpj`} className="block text-xs font-medium text-gray-600 mb-1">CNPJ *</label>
            <input id={`${ncId}-cnpj`} value={form.cnpj} onChange={e => set('cnpj', formatCnpj(e.target.value))}
              placeholder="00.000.000/0001-00"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor={`${ncId}-cidade`} className="block text-xs font-medium text-gray-600 mb-1">Cidade</label>
              <input id={`${ncId}-cidade`} value={form.cidade} onChange={e => set('cidade', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label htmlFor={`${ncId}-estado`} className="block text-xs font-medium text-gray-600 mb-1">Estado</label>
              <input id={`${ncId}-estado`} value={form.estado} onChange={e => set('estado', e.target.value)}
                maxLength={2} placeholder="UF"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase" />
            </div>
          </div>
          <div>
            <label htmlFor={`${ncId}-tel`} className="block text-xs font-medium text-gray-600 mb-1">Telefone</label>
            <input id={`${ncId}-tel`} value={form.telefone_empresa} onChange={e => set('telefone_empresa', e.target.value)}
              placeholder="(00) 00000-0000"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          {error && <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-sm font-medium hover:bg-gray-50">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Salvando...' : 'Cadastrar cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── RazaoSocialAutocomplete ──────────────────────────────────────────────────

export function RazaoSocialAutocomplete({ value, onChange, onSelect, contas = [], className, id }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const refCallback = (el) => {
    ref.current = el;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (el) document.addEventListener('mousedown', handler);
    else document.removeEventListener('mousedown', handler);
  };

  const q = (value || '').toLowerCase();
  const cnpjDigits = q.replace(/\D/g, '');
  const suggestions = q.length < 2 ? [] : contas.filter(c =>
    (c.razao_social || '').toLowerCase().includes(q) ||
    (c.nome_fantasia || '').toLowerCase().includes(q) ||
    (c.nome || '').toLowerCase().includes(q) ||
    (cnpjDigits.length > 0 && (c.cnpj || '').replace(/\D/g, '').includes(cnpjDigits))
  ).slice(0, 8);

  return (
    <div className="relative" ref={refCallback}>
      <input id={id} type="text" value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => value?.length >= 2 && setOpen(true)}
        className={className}
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-52 overflow-y-auto">
          <li className="px-3 py-1.5 bg-gray-50 border-b border-gray-100">
            <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Empresas na base de contas</p>
          </li>
          {suggestions.map(c => (
            <li key={c.id} onMouseDown={e => { e.preventDefault(); onSelect(c); setOpen(false); }}
              className="px-3 py-2 hover:bg-[#f0faff] cursor-pointer border-b border-gray-50 last:border-0 transition-colors">
              <p className="text-xs font-medium text-gray-800 truncate">{c.razao_social || c.nome}</p>
              <p className="text-[11px] text-gray-400 truncate">
                {c.nome_fantasia && c.nome_fantasia !== (c.razao_social || c.nome) && <span className="mr-2">{c.nome_fantasia}</span>}
                {c.cnpj && <span className="mr-2">{c.cnpj}</span>}
                {c.cidade && <span>{c.cidade}{c.estado || c.uf ? `/${c.estado || c.uf}` : ''}</span>}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── LeadSearch ───────────────────────────────────────────────────────────────

export function LeadSearch({ leads = [], onSelect }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const refCallback = (el) => {
    ref.current = el;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (el) document.addEventListener('mousedown', handler);
    else document.removeEventListener('mousedown', handler);
  };

  const filtered = query.length < 2 ? [] : leads.filter(l => {
    const q = query.toLowerCase();
    return (l.razao_social || '').toLowerCase().includes(q) ||
      (l.nome_fantasia || '').toLowerCase().includes(q) ||
      (l.titulo || '').toLowerCase().includes(q) ||
      (l.cnpj || '').replace(/\D/g, '').includes(q.replace(/\D/g, ''));
  }).slice(0, 8);

  return (
    <div className="relative" ref={refCallback}>
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-[#00DBFF]/40 bg-[#f0faff] focus-within:border-[#00DBFF] transition-colors">
        <Search size={14} className="text-[#00DBFF] flex-shrink-0" />
        <input type="text" value={query} onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar lead já cadastrado por CNPJ, razão social ou nome fantasia…"
          className="flex-1 text-xs bg-transparent outline-none text-gray-700 placeholder-gray-400"
        />
        {query && (
          <button type="button" onClick={() => { setQuery(''); setOpen(false); }} className="text-gray-300 hover:text-gray-500">
            <X size={12} />
          </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {filtered.map(l => (
            <button key={l.id} type="button"
              onClick={() => { onSelect(l); setQuery(''); setOpen(false); }}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-[#f0faff] transition-colors text-left border-b border-gray-50 last:border-0">
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-800 truncate">{l.razao_social || l.nome_fantasia || l.titulo}</p>
                <p className="text-[11px] text-gray-400 truncate">
                  {l.cnpj && <span className="mr-2">{l.cnpj}</span>}
                  {l.nome_fantasia && l.razao_social && <span>{l.nome_fantasia}</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#002233] text-white/80 font-medium">{l.stage}</span>
                <ArrowUpRight size={13} className="text-[#00DBFF]" />
              </div>
            </button>
          ))}
        </div>
      )}
      {open && query.length >= 2 && filtered.length === 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2.5">
          <p className="text-xs text-gray-400">Nenhum lead encontrado para "{query}"</p>
        </div>
      )}
    </div>
  );
}

// ─── ContaSearch ──────────────────────────────────────────────────────────────

export function ContaSearch({ contas = [], value, onChange, className, formHints = {}, inputId, ariaLabelledby }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [extraContas, setExtraContas] = useState([]);
  const ref = useRef();

  const allContas = [...contas, ...extraContas];
  const selected = allContas.find(c => c.id === value);

  if (!open && query) setQuery('');

  const refCallback = (el) => {
    ref.current = el;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (el) document.addEventListener('mousedown', handler);
    else document.removeEventListener('mousedown', handler);
  };

  const filtered = query.length < 2 ? [] : allContas.filter(c => {
    const q = query.toLowerCase();
    const cnpjDigits = q.replace(/\D/g, '');
    return c.nome?.toLowerCase().includes(q) || c.razao_social?.toLowerCase().includes(q) ||
      c.nome_fantasia?.toLowerCase().includes(q) ||
      (cnpjDigits.length > 0 && (c.cnpj || '').replace(/\D/g, '').includes(cnpjDigits));
  }).slice(0, 10);

  const displayValue = open ? query : (selected ? (selected.nome || selected.razao_social) : '');

  function handleCreated(result) {
    const nova = { id: result.id, nome: formHints.razao_social || query,
      razao_social: formHints.razao_social || query, cnpj: formHints.cnpj || '',
      cidade: formHints.cidade || '', estado: formHints.estado || '' };
    setExtraContas(prev => [...prev, nova]);
    onChange(result.id); setShowCreate(false); setOpen(false);
  }

  return (
    <>
      <div className="relative" ref={refCallback}>
        <input id={inputId} type="text" value={displayValue}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar por nome, razão social ou CNPJ..."
          className={className} aria-labelledby={ariaLabelledby}
        />
        {selected && !open && (
          <button type="button" onClick={() => onChange(null)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X size={14} />
          </button>
        )}
        {open && filtered.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
            {filtered.map(c => (
              <button key={c.id} type="button"
                className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0"
                onMouseDown={e => e.preventDefault()}
                onClick={() => { onChange(c.id); setOpen(false); }}>
                <div className="font-medium text-gray-900">{c.nome || c.razao_social}</div>
                <div className="text-xs text-gray-400 flex gap-2 mt-0.5">
                  {c.cnpj && <span>{formatCnpj(c.cnpj)}</span>}
                  {c.cidade && <span>· {c.cidade}{c.estado ? `/${c.estado}` : ''}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
        {open && query.length >= 2 && filtered.length === 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow">
            <div className="px-3 py-2.5 text-sm text-gray-400">Nenhuma conta encontrada para "{query}"</div>
            <button type="button" onMouseDown={e => e.preventDefault()}
              onClick={() => { setOpen(false); setShowCreate(true); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-blue-700 hover:bg-blue-50 border-t border-gray-100 rounded-b-lg">
              <Plus size={14} />
              Cadastrar "{query}" como novo cliente de base
            </button>
          </div>
        )}
      </div>
      {showCreate && (
        <NovaContaModal hints={{ ...formHints, razao_social: query || formHints.razao_social }}
          onSave={handleCreated} onClose={() => setShowCreate(false)} />
      )}
    </>
  );
}

// ─── ParcelamentoBlock ────────────────────────────────────────────────────────

export function ParcelamentoBlock({
  label, valor, modo, entradaTipo, entradaValor, saldoParcelas, numParcelas,
  onModo, onEntradaTipo, onEntradaValor, onSaldoParcelas, onNumParcelas, inputClass,
}) {
  const pbId = useId();
  const ic = inputClass || (() => 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg');

  let entradaReais = 0;
  if (modo === 'entrada') {
    entradaReais = entradaTipo === 'pct'
      ? valor * ((Number(entradaValor) || 0) / 100)
      : Number(entradaValor) || 0;
  }
  const saldo = valor - entradaReais;
  const parcelaSaldo = saldo > 0 && (saldoParcelas || 1) > 0 ? saldo / (saldoParcelas || 1) : 0;
  const parcelaIgual = numParcelas > 0 ? valor / numParcelas : valor;

  return (
    <div className="border border-gray-200 rounded-lg p-3 space-y-3 bg-gray-50/50">
      <p className="text-xs font-semibold text-[#535c69] uppercase tracking-wide">{label}</p>
      <div className="flex gap-2">
        {['igual', 'entrada'].map(m => (
          <button key={m} type="button" onClick={() => onModo(m)}
            className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              modo === m ? 'bg-[#00DBFF] border-[#00DBFF] text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>
            {m === 'igual' ? 'Parcelas iguais' : 'Entrada + saldo'}
          </button>
        ))}
      </div>
      {modo === 'igual' && (
        <div>
          <label htmlFor={`${pbId}-num`} className="block text-xs font-medium text-gray-600 mb-1">Número de parcelas</label>
          <input id={`${pbId}-num`} type="number" min="1" max="60" value={numParcelas || 1}
            onChange={e => onNumParcelas(Math.max(1, parseInt(e.target.value) || 1))} className={ic('num_parcelas')} />
          {numParcelas > 1 && <p className="mt-1 text-xs text-[#007a91]">{numParcelas}x de {formatBRL(parcelaIgual)}</p>}
        </div>
      )}
      {modo === 'entrada' && (
        <div className="space-y-3">
          <div>
            <label htmlFor={`${pbId}-entrada`} className="block text-xs font-medium text-gray-600 mb-1">Entrada</label>
            <div className="flex gap-2">
              <div className="flex border border-gray-300 rounded-lg overflow-hidden text-xs">
                {['pct', 'reais'].map(t => (
                  <button key={t} type="button" onClick={() => onEntradaTipo(t)}
                    className={`px-2.5 py-1.5 font-medium transition-colors ${entradaTipo === t ? 'bg-[#00DBFF] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    {t === 'pct' ? '%' : 'R$'}
                  </button>
                ))}
              </div>
              <input id={`${pbId}-entrada`} type="number" min="0"
                max={entradaTipo === 'pct' ? 100 : undefined}
                step={entradaTipo === 'pct' ? 1 : 0.01}
                placeholder={entradaTipo === 'pct' ? 'Ex: 30' : 'Ex: 5000'}
                value={entradaValor ?? ''}
                onChange={e => onEntradaValor(e.target.value === '' ? null : Number(e.target.value))}
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#00DBFF]"
              />
            </div>
            {entradaReais > 0 && <p className="mt-1 text-xs text-[#007a91]">Entrada: {formatBRL(entradaReais)}</p>}
          </div>
          <div>
            <label htmlFor={`${pbId}-saldo`} className="block text-xs font-medium text-gray-600 mb-1">
              Saldo {saldo > 0 ? `(${formatBRL(saldo)})` : ''} — número de parcelas
            </label>
            <input id={`${pbId}-saldo`} type="number" min="1" max="60" value={saldoParcelas || 1}
              onChange={e => onSaldoParcelas(Math.max(1, parseInt(e.target.value) || 1))} className={ic('saldo_parcelas')} />
            {saldo > 0 && (saldoParcelas || 1) > 1 && (
              <p className="mt-1 text-xs text-[#007a91]">{saldoParcelas}x de {formatBRL(parcelaSaldo)}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
